"use client";

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ComponentPropsWithoutRef, ReactNode, RefObject } from "react";
import type { Media } from "@mw/shared/payload-types";
import { useFocus, useHover } from "@react-aria/interactions";
import { Image } from "@ui/components/core/Image";
import { Link } from "@ui/components/core/Link";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { mergeProps } from "react-aria";
import clsx from "clsx";
import { cva } from "class-variance-authority";

import { Block } from "../Block";

export type IntegrationsGridItem = {
  iconGrayscale: Media | string;
  iconWhite: Media | string;
  id: string;
  slug: string | null | undefined;
  title: string;
  description: ReactNode;
  brandColor: string;
};

type GridPosition = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export type BlockIntegrationsGridClientProps = {
  integrations: IntegrationsGridItem[];
};

type HoveredInfo = {
  integration: IntegrationsGridItem;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  isLeftHalf: boolean;
  row: number;
  col: number;
  totalRows: number;
  totalColumns: number;
};

const DESKTOP_PATTERN = {
  pattern: [
    [0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 1, 0, 0],
    [0, 1, 0, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 0, 0, 1, 0, 0],
    [0, 0, 0, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0],
  ],
} as const;

const MOBILE_PATTERN = {
  pattern: [
    [0, 1, 0, 0, 0],
    [0, 0, 1, 0, 0],
    [1, 0, 0, 0, 1],
    [0, 1, 0, 1, 0],
    [0, 0, 1, 0, 0],
  ],
} as const;

const distributeItems = (count: number, isMobile: boolean) => {
  const patternGrid = isMobile ? MOBILE_PATTERN.pattern : DESKTOP_PATTERN.pattern;

  const positions: Array<{ row: number; col: number }> = [];

  for (let rowIndex = 0; rowIndex < patternGrid.length; rowIndex++) {
    for (let colIndex = 0; colIndex < patternGrid[rowIndex].length; colIndex++) {
      if (patternGrid[rowIndex][colIndex] === 1) {
        positions.push({
          row: rowIndex + 1,
          col: colIndex + 1,
        });
      }
    }
  }

  return positions.slice(0, count);
};

const getGridDimensions = (isMobile: boolean) => {
  const patternGrid = isMobile ? MOBILE_PATTERN.pattern : DESKTOP_PATTERN.pattern;

  return {
    rows: patternGrid.length,
    columns: patternGrid[0].length,
  };
};

const generateLogoItems = (logos: IntegrationsGridItem[]) => {
  const mobilePositions = distributeItems(logos.length, true);
  const desktopPositions = distributeItems(logos.length, false);
  const mobileGrid = getGridDimensions(true);
  const desktopGrid = getGridDimensions(false);

  const logoItems = logos
    .map((logo, index) => {
      const mobilePos = mobilePositions[index];
      const desktopPos = desktopPositions[index];

      if (!mobilePos || !desktopPos) return null;

      return {
        logo,
        mobileRow: mobilePos.row,
        mobileCol: mobilePos.col,
        desktopRow: desktopPos.row,
        desktopCol: desktopPos.col,
        zIndex: index,
      };
    })
    .filter(Boolean) as Array<{
    logo: IntegrationsGridItem;
    mobileRow: number;
    mobileCol: number;
    desktopRow: number;
    desktopCol: number;
    zIndex: number;
  }>;

  return { logoItems, mobileGrid, desktopGrid };
};

const gridPositionVariants = cva("", {
  variants: {
    mobileCol: {
      1: "col-start-1",
      2: "col-start-2",
      3: "col-start-3",
      4: "col-start-4",
      5: "col-start-5",
      6: "col-start-6",
      7: "col-start-7",
      8: "col-start-8",
    },
    mobileRow: {
      1: "row-start-1",
      2: "row-start-2",
      3: "row-start-3",
      4: "row-start-4",
      5: "row-start-5",
      6: "row-start-6",
      7: "row-start-7",
      8: "row-start-8",
    },
    desktopCol: {
      1: "md:col-start-1",
      2: "md:col-start-2",
      3: "md:col-start-3",
      4: "md:col-start-4",
      5: "md:col-start-5",
      6: "md:col-start-6",
      7: "md:col-start-7",
      8: "md:col-start-8",
    },
    desktopRow: {
      1: "md:row-start-1",
      2: "md:row-start-2",
      3: "md:row-start-3",
      4: "md:row-start-4",
      5: "md:row-start-5",
      6: "md:row-start-6",
      7: "md:row-start-7",
      8: "md:row-start-8",
    },
  },
});

const gridBackgroundStyles = {
  backgroundImage: `
    linear-gradient(to right, transparent calc(100% - 1px), rgba(0, 0, 0, 0.07) calc(100% - 1px)),
    linear-gradient(to bottom, transparent calc(100% - 1px), rgba(0, 0, 0, 0.07) calc(100% - 1px))
  `,
  maskImage: `
    linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%),
    linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)
  `,
  WebkitMaskImage: `
    linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%),
    linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)
  `,
  maskComposite: "intersect" as const,
  WebkitMaskComposite: "source-in" as const,
};

const LogoButton = ({
  integration,
  onHoverChange,
  isHovered,
  hasAnyActive,
  zIndex,
  position,
  containerRef,
}: {
  integration: IntegrationsGridItem;
  onHoverChange: (hoveredInfo: HoveredInfo | null) => void;
  isHovered: boolean;
  hasAnyActive: boolean;
  zIndex: number;
  position: {
    mobile: { row: number; col: number; totalRows: number; totalColumns: number };
    desktop: { row: number; col: number; totalRows: number; totalColumns: number };
  };
  containerRef: RefObject<HTMLDivElement | null>;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();
  const titleId = `integration-${integration.id}-title`;
  const descriptionId = `integration-${integration.id}-description`;

  const triggerHover = useCallback(() => {
    if (!wrapperRef.current || !containerRef.current) return;

    const buttonRect = wrapperRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const isDesktop = window.innerWidth >= 768;
    const currentPosition = isDesktop ? position.desktop : position.mobile;
    const relativeX = buttonRect.left - containerRect.left;
    const relativeY = buttonRect.top - containerRect.top;
    const isLeftHalf = currentPosition.col <= currentPosition.totalColumns / 2;

    onHoverChange({
      integration,
      x: relativeX,
      y: relativeY,
      width: buttonRect.width,
      height: buttonRect.height,
      color: integration.brandColor,
      isLeftHalf,
      row: currentPosition.row,
      col: currentPosition.col,
      totalRows: currentPosition.totalRows,
      totalColumns: currentPosition.totalColumns,
    });
  }, [containerRef, integration, onHoverChange, position.desktop, position.mobile]);

  const { hoverProps } = useHover({
    onHoverStart: triggerHover,
    onHoverEnd: () => onHoverChange(null),
  });

  const { focusProps } = useFocus({
    onFocus: triggerHover,
    onBlur: () => onHoverChange(null),
  });

  const href = integration.slug ? `/agents/integrations/${integration.slug}` : "#";
  const isActive = isHovered;
  const interactionProps = mergeProps(hoverProps, focusProps) as ComponentPropsWithoutRef<"a">;

  return (
    <div
      ref={wrapperRef}
      style={{
        transformStyle: "preserve-3d",
        transform: `translateZ(${zIndex * 50}px)`,
      }}
      className={clsx(
        "relative h-full w-full transition-opacity duration-300",
        hasAnyActive && !isActive ? "opacity-20" : "opacity-100",
      )}
    >
      <Link
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
        href={href}
        variant='clean'
        className='group relative block h-full w-full'
        {...interactionProps}
      >
        <span id={titleId} className='sr-only'>
          {integration.title}
        </span>
        <span id={descriptionId} className='sr-only'>
          {integration.description}
        </span>
        <div
          className='absolute inset-0 transition-colors duration-300'
          style={{
            backgroundColor: isActive ? integration.brandColor : "transparent",
          }}
        />
        <div className='relative h-full w-full'>
          <Image
            alt={t("ClientKeys.IntegrationsGrid.integration_alt", { title: integration.title })}
            className={clsx(
              "absolute inset-0 h-full w-full transition-opacity duration-300",
              isActive ? "opacity-0" : "opacity-100",
            )}
            crossOrigin='anonymous'
            fit='cover'
            quality={100}
            resource={integration.iconGrayscale}
            sizes='(max-width: 768px) 80px, 120px'
          />
          <Image
            alt={t("ClientKeys.IntegrationsGrid.integration_alt", { title: integration.title })}
            className={clsx(
              "absolute inset-0 h-full w-full transition-opacity duration-300",
              isActive ? "opacity-100" : "opacity-0",
            )}
            crossOrigin='anonymous'
            fit='cover'
            quality={100}
            resource={integration.iconWhite}
            sizes='(max-width: 768px) 80px, 120px'
          />
        </div>
      </Link>
    </div>
  );
};

const LINE_HEIGHT_DESKTOP = 140;
const LINE_HEIGHT_MOBILE = 60;
const HORIZONTAL_GAP = 16;

const ConnectingLine = ({
  x,
  y,
  width,
  color,
  integrationId,
  containerWidth,
  containerHeight,
  lineHeight,
  row,
  col,
  totalRows,
  totalColumns,
}: {
  x: number;
  y: number;
  width: number;
  color: string;
  integrationId: string;
  containerWidth: number;
  containerHeight: number;
  lineHeight: number;
  row: number;
  col: number;
  totalRows: number;
  totalColumns: number;
}) => {
  const rowProgress = row / totalRows;
  const colProgress = col / totalColumns;
  const perspectiveOffset = rowProgress * 8 - colProgress * 8;
  const centerX = Math.round(x + width / 2) + perspectiveOffset;
  const gradientId = `line-gradient-${integrationId}`;
  const startY = y;
  const endY = y - lineHeight;

  return (
    <m.svg
      className='pointer-events-none absolute inset-0 z-10 hidden md:block'
      width={containerWidth}
      height={containerHeight}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <defs>
        <linearGradient id={gradientId} x1={centerX} y1={startY} x2={centerX} y2={endY} gradientUnits='userSpaceOnUse'>
          <stop offset='0%' stopColor={color} stopOpacity='1' />
          <stop offset='40%' stopColor='#000000' stopOpacity='0.4' />
        </linearGradient>
      </defs>
      <m.line
        x1={centerX}
        y1={startY}
        x2={centerX}
        y2={endY}
        stroke={`url(#${gradientId})`}
        strokeWidth='1'
        strokeLinecap='round'
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        exit={{ pathLength: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      />
    </m.svg>
  );
};

const Grid = ({
  logoItems,
  mobileGrid,
  desktopGrid,
  hoveredInfo,
  onHoverChange,
  containerRef,
}: {
  logoItems: Array<{
    logo: IntegrationsGridItem;
    mobileRow: number;
    mobileCol: number;
    desktopRow: number;
    desktopCol: number;
    zIndex: number;
  }>;
  mobileGrid: { rows: number; columns: number };
  desktopGrid: { rows: number; columns: number };
  hoveredInfo: HoveredInfo | null;
  onHoverChange: (hoveredInfo: HoveredInfo | null) => void;
  containerRef: RefObject<HTMLDivElement | null>;
}) => {
  return (
    <div
      className='relative'
      style={{
        transform: "rotateX(45deg) rotateZ(45deg)",
        transformStyle: "preserve-3d",
      }}
    >
      <div
        className='pointer-events-none absolute z-1 h-full w-full'
        style={{
          backgroundImage: "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(253,252,252,0) 81%, var(--eggshell) 100%)",
        }}
      />
      <div
        className='absolute inset-0 z-0 bg-[length:20%_20%] md:bg-[length:12.5%_12.5%]'
        style={gridBackgroundStyles}
      />
      <div className='relative z-10 grid aspect-1x1 w-full grid-cols-5 grid-rows-5 gap-[1.5] md:grid-cols-8 md:grid-rows-8'>
        {logoItems.map(item => {
          const isHovered =
            hoveredInfo !== null &&
            hoveredInfo.integration.id !== undefined &&
            item.logo.id !== undefined &&
            hoveredInfo.integration.id === item.logo.id;

          return (
            <div
              key={item.logo.id}
              className={gridPositionVariants({
                mobileCol: item.mobileCol as GridPosition,
                mobileRow: item.mobileRow as GridPosition,
                desktopCol: item.desktopCol as GridPosition,
                desktopRow: item.desktopRow as GridPosition,
              })}
            >
              <LogoButton
                integration={item.logo}
                onHoverChange={onHoverChange}
                isHovered={isHovered}
                hasAnyActive={Boolean(hoveredInfo)}
                zIndex={item.zIndex}
                position={{
                  mobile: {
                    row: item.mobileRow,
                    col: item.mobileCol,
                    totalRows: mobileGrid.rows,
                    totalColumns: mobileGrid.columns,
                  },
                  desktop: {
                    row: item.desktopRow,
                    col: item.desktopCol,
                    totalRows: desktopGrid.rows,
                    totalColumns: desktopGrid.columns,
                  },
                }}
                containerRef={containerRef}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HoverOverlay = ({
  hoveredInfo,
  containerRef,
}: {
  hoveredInfo: HoveredInfo;
  containerRef: RefObject<HTMLDivElement | null>;
}) => {
  const [layoutData, setLayoutData] = useState<{
    containerWidth: number;
    containerHeight: number;
    lineHeight: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const updateLayout = () => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      const lineHeight = isMobile ? LINE_HEIGHT_MOBILE : LINE_HEIGHT_DESKTOP;

      setLayoutData({
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
        lineHeight,
      });
    };

    updateLayout();

    window.addEventListener("resize", updateLayout);

    return () => window.removeEventListener("resize", updateLayout);
  }, [containerRef]);

  if (!layoutData) return null;

  const centerX = Math.round(hoveredInfo.x + hoveredInfo.width / 2) - 0.75;
  const infoLeft = hoveredInfo.isLeftHalf ? centerX + HORIZONTAL_GAP : centerX - HORIZONTAL_GAP;

  return (
    <m.div key={hoveredInfo.integration.id}>
      <ConnectingLine
        x={hoveredInfo.x}
        y={hoveredInfo.y}
        width={hoveredInfo.width}
        color={hoveredInfo.color}
        integrationId={hoveredInfo.integration.id}
        containerWidth={layoutData.containerWidth}
        containerHeight={layoutData.containerHeight}
        lineHeight={layoutData.lineHeight}
        row={hoveredInfo.row}
        col={hoveredInfo.col}
        totalRows={hoveredInfo.totalRows}
        totalColumns={hoveredInfo.totalColumns}
      />

      <m.div
        aria-hidden='true'
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.3, delay: 0.15, ease: "easeOut" } }}
        exit={{ opacity: 0, y: 10, transition: { duration: 0.2, ease: "easeOut" } }}
        className='pointer-events-none absolute hidden max-w-xs md:block'
        style={{
          zIndex: 11,
          [hoveredInfo.isLeftHalf ? "left" : "right"]: hoveredInfo.isLeftHalf
            ? `${infoLeft}px`
            : `${layoutData.containerWidth - infoLeft}px`,
          top: `${hoveredInfo.y - layoutData.lineHeight}px`,
        }}
      >
        <h3 className='f-paragraph-03 line-clamp-1 md:f-paragraph-02'>{hoveredInfo.integration.title}</h3>
        {hoveredInfo.integration.description}
      </m.div>
    </m.div>
  );
};

export const BlockIntegrationsGridClient = ({ integrations }: BlockIntegrationsGridClientProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredInfo, setHoveredInfo] = useState<HoveredInfo | null>(null);
  const { logoItems, mobileGrid, desktopGrid } = useMemo(() => generateLogoItems(integrations), [integrations]);

  return (
    <Block name='integrations-grid'>
      <div ref={containerRef} className='relative mx-auto w-full max-w-5xl overflow-hidden pt-20 md:pt-0'>
        <div
          className='relative mx-auto max-w-sm md:max-w-3xl'
          style={{
            perspective: "3000px",
            perspectiveOrigin: "bottom",
          }}
        >
          <Grid
            logoItems={logoItems}
            mobileGrid={mobileGrid}
            desktopGrid={desktopGrid}
            hoveredInfo={hoveredInfo}
            onHoverChange={setHoveredInfo}
            containerRef={containerRef}
          />
        </div>

        <AnimatePresence>
          {hoveredInfo && <HoverOverlay hoveredInfo={hoveredInfo} containerRef={containerRef} />}
        </AnimatePresence>
      </div>
    </Block>
  );
};
